import { useRef } from 'react';
import { buildApiUrl } from '../services/api';
import { PaymentFormData } from '../types/payment';

const normalizeDigits = (value: string) => value.replace(/\D/g, '');

export function usePagBank() {
  const pagbankScriptRef = useRef<Promise<void> | null>(null);
  const pagbankPublicKeyRef = useRef<Promise<string> | null>(null);

  const getPagBankPublicKey = async () => {
    const envKey = import.meta.env.VITE_PAGBANK_PUBLIC_KEY;
    if (envKey) {
      return envKey;
    }

    if (!pagbankPublicKeyRef.current) {
      pagbankPublicKeyRef.current = (async () => {
        const response = await fetch(buildApiUrl('/pagbank/public-key'));
        const data = await response.json().catch(() => null);
        const key = data?.publicKey || data?.public_key || data?.key;

        if (!response.ok || !key) {
          throw new Error(data?.message || data?.error || 'Não foi possível obter a chave de criptografia');
        }

        return key as string;
      })();
    }

    try {
      return await pagbankPublicKeyRef.current;
    } catch (error) {
      pagbankPublicKeyRef.current = null;
      throw error;
    }
  };

  const loadPagBankSdk = async () => {
    if (typeof window === 'undefined') {
      throw new Error('Ambiente inválido para criptografia do cartão');
    }

    if (window.PagSeguro?.encryptCard) {
      return;
    }

    const envSdkUrl = import.meta.env.VITE_PAGBANK_JS_URL;
    const candidateUrls = envSdkUrl
      ? [envSdkUrl]
      : [
          'https://assets.pagseguro.com.br/checkout-sdk-js/2.0.0/pagseguro.min.js',
          'https://assets.pagseguro.com.br/checkout-sdk-js/2.0.0/pagseguro.js'
        ];

    const loadScript = (url: string) =>
      new Promise<void>((resolve, reject) => {
        const existing = document.querySelector(`script[data-pagbank-sdk=\"true\"][data-src=\"${url}\"]`);
        if (existing) {
          const loaded = (existing as HTMLScriptElement).dataset.loaded === 'true';
          if (loaded) {
            resolve();
            return;
          }
          existing.addEventListener('load', () => resolve());
          existing.addEventListener('error', () => reject(new Error('Falha ao carregar PagBank JS')));
          return;
        }

        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.dataset.pagbankSdk = 'true';
        script.dataset.src = url;
        script.onload = () => {
          script.dataset.loaded = 'true';
          resolve();
        };
        script.onerror = () => {
          script.remove();
          reject(new Error('Falha ao carregar PagBank JS'));
        };
        document.body.appendChild(script);
      });

    if (!pagbankScriptRef.current) {
      pagbankScriptRef.current = (async () => {
        let lastError: Error | null = null;
        for (const url of candidateUrls) {
          try {
            await loadScript(url);
            if (window.PagSeguro?.encryptCard) {
              return;
            }
          } catch (error) {
            lastError = error instanceof Error ? error : new Error('Falha ao carregar PagBank JS');
          }
        }
        throw lastError || new Error('Falha ao carregar PagBank JS');
      })();
    }

    await pagbankScriptRef.current;

    if (!window.PagSeguro?.encryptCard) {
      throw new Error('SDK do PagBank não está disponível');
    }
  };

  const encryptCard = async (paymentData: PaymentFormData) => {
    const publicKey = await getPagBankPublicKey();
    await loadPagBankSdk();

    const expiry = paymentData.cardExpiry.split('/');
    const expMonth = (expiry[0] || '').padStart(2, '0');
    const expYearRaw = expiry[1] || '';
    const expYear = expYearRaw.length === 2 ? `20${expYearRaw}` : expYearRaw;

    if (!expMonth || !expYear) {
      throw new Error('Validade inválida');
    }

    const result = window.PagSeguro?.encryptCard({
      publicKey,
      holder: paymentData.holderName,
      number: normalizeDigits(paymentData.cardNumber),
      expMonth,
      expYear,
      securityCode: normalizeDigits(paymentData.cardCvv)
    });

    if (!result?.encryptedCard) {
      const message = result?.errors?.[0]?.message || 'Não foi possível criptografar o cartão';
      throw new Error(message);
    }

    return result.encryptedCard;
  };

  return {
    encryptCard
  };
}
